import { Component, DestroyRef, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, of, switchMap } from 'rxjs';
import { ProductCard } from '../../../shared/product-card/product-card';
import { DevFlag } from '../../../shared/dev-flag/dev-flag';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { SeoService } from '../../../core/seo/seo.service';
import { SsrResponseService } from '../../../core/seo/ssr-response.service';
import { breadcrumbSchemaFor, breadcrumbTrail } from '../../../core/seo/breadcrumb-trail';
import { faqPageSchema, webPageSchema } from '../../../core/seo/structured-data';
import { Breadcrumb } from '../../../shared/breadcrumb/breadcrumb';
import { StoreLogo } from '../../../shared/store-logo/store-logo';
import { AutoScrollRow } from '../../../shared/auto-scroll-row/auto-scroll-row';
import { SavingArenaApiService } from '../../../core/api/saving-arena-api.service';
import { SAVING_ARENA_API_CONFIG } from '../../../core/api/api.config';
import {
  FrontendDealsEvent,
  FrontendDealsCategoryFaq,
  FrontendDealsStoreEditorial,
  FrontendProduct,
} from '../../../core/api/api.models';
import { TrustedCmsHtmlPipe } from '../../../core/api/trusted-cms-html.pipe';

// Products load a page at a time behind "Load more" — an event can hold well over a thousand,
// so rendering them all at once would be unusable. 24 = six full rows of the four-across grid.
const PRODUCT_PAGE_SIZE = 24;
/** Logos in the "<Category> Top Stores" strip — one full row in the existing layout. */
const TOP_STORE_LIMIT = 8;

/** One product strip on the page. Mirrors the deals-store page's ProductSection. */
interface EventSection {
  /** Stable id used to update this section in place. */
  id: string;
  heading: string;
  /** Store id to narrow to, '' for every store in the event. */
  store: string;
  sort: 'discount' | 'newest';
  products: EventArrival[];
  total: number;
  page: number;
  pages: number;
  loading: boolean;
  /** Whether this section's first page has been fetched yet. */
  loaded: boolean;
  /**
   * The two derived strips load on page init so the top of the page is server-rendered. The
   * per-store sections wait until scrolled near, keeping the initial DOM and request count small.
   */
  eager: boolean;
}

interface EventArrival {
  /** Product id, for the `/go/p/:id` "Buy Now" redirect. */
  id: string;
  title: string;
  /** Route to this product's own page, '' when the product has no slug. */
  detailPath: string;
  image: string;
  price: string;
  oldPrice: string;
  storeName: string;
  storeLogoUrl: string;
  /** Whether the product goes off-site, so the card can build its own /go/p/ redirect. */
  isExternal: boolean;
}

@Component({
  selector: 'app-deals-event-page',
  imports: [
    Breadcrumb,
    ProductCard,
    DevFlag,
    TrustedCmsHtmlPipe,
    RouterLink,
    StoreLogo,
    AutoScrollRow,
  ],
  templateUrl: './deals-event-page.html',
  styleUrl: './deals-event-page.scss',
})
export class DealsEventPage {
  private readonly route = inject(ActivatedRoute);
  private readonly seo = inject(SeoService);
  private readonly ssrResponse = inject(SsrResponseService);
  private readonly api = inject(SavingArenaApiService);
  private readonly apiConfig = inject(SAVING_ARENA_API_CONFIG);
  private readonly destroyRef = inject(DestroyRef);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private lazyObserver?: IntersectionObserver;

  // Admin-authored content for this event. null until it loads (or when the event has no page).
  private readonly dealsEvent = signal<FrontendDealsEvent | null>(null);
  private readonly slug = signal('');

  // Event name: the record first, else a title-cased slug so the page always has a heading.
  protected readonly crumbs = computed(() => {
    const name = this.eventName();
    return breadcrumbTrail(
      { label: 'Deals', path: '/collection' },
      ...(name ? [{ label: name }] : []),
    );
  });

  protected readonly eventName = computed(() => {
    const fromApi = this.dealsEvent()?.event?.name?.trim();
    if (fromApi) return fromApi;
    return this.slug()
      .split('-')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  });

  // Hero banner (dynamic when the admin uploaded one; the template keeps its static banner otherwise).
  protected readonly heroBannerUrl = computed(() =>
    this.assetUrl(this.dealsEvent()?.hero?.bannerImagePath ?? ''),
  );
  protected readonly heroAltText = computed(
    () => this.dealsEvent()?.hero?.bannerAltText || `${this.eventName()} deals`,
  );

  // Editorial lookbook, image paths resolved to absolute URLs (mirrors the deals-store mapping).
  protected readonly editorial = computed<FrontendDealsStoreEditorial | null>(() => {
    const data = this.dealsEvent()?.editorial;
    if (!data) return null;
    const map = (img: { imageUrl: string; url: string } | null) =>
      img ? { imageUrl: this.assetUrl(img.imageUrl), url: img.url } : null;
    return {
      full: map(data.full),
      fullHeading: data.fullHeading,
      half: map(data.half),
      collage: data.collage.map((item) => ({
        imageUrl: this.assetUrl(item.imageUrl),
        url: item.url,
      })),
      collageHeading: data.collageHeading,
    };
  });

  protected readonly longContent = computed(() => this.dealsEvent()?.longContent?.trim() || '');
  protected readonly longHeading = computed(() => this.dealsEvent()?.longHeading?.trim() || '');
  protected readonly longIntro = computed(() => this.dealsEvent()?.longIntro?.trim() || '');

  protected readonly faqs = computed<FrontendDealsCategoryFaq[]>(() => {
    const list = this.dealsEvent()?.faqs ?? [];
    return list.filter((f) => f.question || f.answer);
  });

  /**
   * The page is a stack of product sections rather than one flat grid, mirroring the deals-store
   * page: a discounted strip, a new-arrivals strip, then one section per store that carries this
   * event. Each keeps its own paging so "load more" in one does not disturb the others.
   */
  protected readonly sections = signal<EventSection[]>([]);

  /**
   * "More <Event> Brands to Explore" — the stores taking part in this event, each
   * linking to its deals page. Split into five columns to fill the existing grid; a store with no
   * slug is dropped since it has no reachable page.
   */
  protected readonly brandColumns = computed<{ label: string; path: string }[][]>(() => {
    // `storeKey` (domain-derived), not the coupon slug — that is the key `/deals/:storeName` uses
    // everywhere else on the site, e.g. /deals/wmarksandspencer.ie.
    const brands = (this.dealsEvent()?.brands ?? [])
      .filter((brand) => brand.label && brand.storeKey)
      .map((brand) => ({
        label: brand.label,
        path: this.storePath(brand.storeKey, brand.categorySlug),
      }));

    if (!brands.length) return [];

    const columnCount = Math.min(5, brands.length);
    const perColumn = Math.ceil(brands.length / columnCount);
    return Array.from({ length: columnCount }, (_, index) =>
      brands.slice(index * perColumn, (index + 1) * perColumn),
    ).filter((column) => column.length > 0);
  });

  protected readonly hasBrands = computed(() => this.brandColumns().length > 0);

  /**
   * "<Category> Top Stores" — the logo strip. Same source as the nav (stores that actually carry
   * this event, richest first), capped at the row width the layout was designed for. Stores
   * without a logo are dropped rather than shown as a broken image; the section hides entirely when
   * nothing is left, since a strip of blanks reads worse than no strip.
   */
  protected readonly topStores = computed<{ label: string; path: string; logoUrl: string }[]>(() =>
    (this.dealsEvent()?.nav ?? [])
      .filter((store) => store.label && store.storeKey && store.logoImagePath)
      .slice(0, TOP_STORE_LIMIT)
      .map((store) => ({
        label: store.label,
        path: this.storePath(store.storeKey, store.categorySlug),
        logoUrl: this.assetUrl(store.logoImagePath),
      })),
  );
  constructor() {
    this.route.paramMap
      .pipe(
        switchMap((params) => {
          const slug = params.get('eventSlug')?.trim() || '';
          this.slug.set(slug);
          this.dealsEvent.set(null);
          this.sections.set([]);
          this.applySeo();
          return this.api.getDealsEvent(slug).pipe(
            catchError((error: unknown) => {
              this.ssrResponse.noteFetchFailure(error);
              return of(null);
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((res) => {
        const event = res?.data ?? null;
        this.dealsEvent.set(event);

        // A slug that matches no event is a real 404, not a 200 rendering an empty page.
        //
        // Every `/deals/event/<anything>` answered 200 — the lookup failed, the page rendered its
        // empty state, and the status stayed OK. That is a soft 404, and it makes an unlimited
        // supply of junk URLs indexable, each one a near-duplicate of the others.
        if (!event) {
          this.ssrResponse.setNotFound();
          this.seo.apply({
            title: 'Event not found | SavingArena',
            description: 'The event you are looking for is not available on SavingArena.',
            noindex: true,
            // No canonical on a dead URL — it would nominate the address as the preferred version
            // of itself while the status says it is not there.
            noCanonical: true,
          });
          this.seo.setJsonLd([]);
          return;
        }

        this.applySeo();
        this.buildSections();
      });
  }

  /**
   * Builds the section list once the event is known: the two derived strips first, then one per
   * store in the event nav. Only the eager strips fetch now; the rest load when scrolled near.
   */
  private buildSections(): void {
    const data = this.dealsEvent();
    const eventId = data?.event?.id?.trim() ?? '';
    if (!eventId) {
      this.sections.set([]);
      return;
    }

    const name = data?.event?.name?.trim() || 'This event';
    const sections: EventSection[] = [
      {
        id: 'discounted',
        heading: `Recommended Discounted ${name} Products For You`,
        store: '',
        sort: 'discount',
        products: [],
        total: 0,
        page: 1,
        pages: 1,
        loading: true,
        loaded: false,
        eager: true,
      },
      {
        id: 'new-arrivals',
        heading: 'New Arrivals',
        store: '',
        sort: 'newest',
        products: [],
        total: 0,
        page: 1,
        pages: 1,
        loading: true,
        loaded: false,
        eager: true,
      },
      ...(data?.nav ?? []).map((tab) => ({
        id: `store-${tab.key}`,
        heading: tab.label,
        store: tab.key,
        sort: 'newest' as const,
        products: [],
        total: tab.productCount ?? 0,
        page: 1,
        pages: 1,
        loading: false,
        loaded: false,
        eager: false,
      })),
    ];

    this.sections.set(sections);
    sections
      .filter((section) => section.eager)
      .forEach((section) => this.loadSection(section.id, 1));
    this.observeLazySections();
  }

  /** Page 1 replaces a section's grid; later pages append to it. */
  private loadSection(sectionId: string, page: number): void {
    const section = this.sections().find((item) => item.id === sectionId);
    const eventId = this.dealsEvent()?.event?.id?.trim() ?? '';
    if (!section || !eventId) return;

    this.patchSection(sectionId, { loading: true });

    this.api
      .getProducts({
        event: eventId,
        store: section.store || undefined,
        sort: section.sort === 'discount' ? 'discount' : undefined,
        page,
        limit: PRODUCT_PAGE_SIZE,
      })
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((res) => {
        const items = (res?.data ?? [])
          .map((product) => this.toEventArrival(product))
          .filter((item) => item.title.length > 0);

        this.patchSection(sectionId, {
          products: page === 1 ? items : [...section.products, ...items],
          total: res?.pagination?.total ?? items.length,
          page: res?.pagination?.page ?? page,
          pages: res?.pagination?.pages ?? page,
          loading: false,
          loaded: true,
        });
      });
  }

  private patchSection(sectionId: string, patch: Partial<EventSection>): void {
    this.sections.update((sections) =>
      sections.map((section) => (section.id === sectionId ? { ...section, ...patch } : section)),
    );
  }

  protected hasMore(section: EventSection): boolean {
    return section.loaded && section.page < section.pages;
  }

  /** "Load more" for one section — the others are untouched. */
  protected loadMoreProducts(section: EventSection): void {
    if (section.loading || !this.hasMore(section)) return;
    this.loadSection(section.id, section.page + 1);
  }

  // Loads each not-yet-fetched store section just before it scrolls into view, so an event with a
  // dozen stores does not fire a dozen requests up front. Client-only; the server renders the
  // placeholders and the browser takes over.
  private observeLazySections(): void {
    if (!this.isBrowser) return;

    this.lazyObserver?.disconnect();
    this.lazyObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const id = (entry.target as HTMLElement).dataset['lazySection'];
          this.lazyObserver?.unobserve(entry.target);
          if (id) this.loadSection(id, 1);
        }
      },
      { rootMargin: '500px 0px' },
    );

    setTimeout(() => {
      document
        .querySelectorAll('[data-lazy-section]')
        .forEach((el) => this.lazyObserver?.observe(el));
    });
  }

  private toEventArrival(product: FrontendProduct): EventArrival {
    const store = product.store;
    const imagePath = product.thumbnailImagePath?.trim() ?? '';
    const logoPath = store?.logoImagePath?.trim() ?? '';
    const oldPrice = product.oldPrice?.trim() ?? '';

    const slug = product.slug?.trim() ?? '';

    return {
      id: String(product.id ?? ''),
      title: product.title?.trim() || product.name?.trim() || '',
      detailPath: slug ? `/${slug}` : '',
      image: imagePath ? this.assetUrl(imagePath) : '',
      price: product.newPrice?.trim() ?? '',
      oldPrice,
      storeName: store?.name?.trim() || '',
      storeLogoUrl: logoPath ? this.assetUrl(logoPath) : '',
      isExternal: product.isExternal === true,
    };
  }

  // Absolute asset URL — same rule as AssetUrlPipe (passes http(s) through, else prefixes assetBaseUrl).
  private assetUrl(path: string): string {
    const value = (path || '').trim();
    if (!value) return '';
    if (/^https?:\/\//i.test(value)) return value;
    const base = this.apiConfig.assetBaseUrl.replace(/\/$/, '');
    return `${base}${value.startsWith('/') ? value : `/${value}`}`;
  }

  private applySeo(): void {
    const name = this.eventName();
    const path = `/deals/event/${this.slug()}`;
    const url = this.seo.absolute(path);
    const title = `${name} Deals & Offers | SavingArena`;
    const description = `Browse the latest ${name.toLowerCase()} deals, discounts and product offers curated by SavingArena.`;

    this.seo.apply({ title, description, canonicalPath: path });

    const schema = [
      webPageSchema({ origin: this.seo.origin, url, name: title, description }),
      breadcrumbSchemaFor(this.crumbs(), (p) => this.seo.absolute(p)),
    ];
    const faqSchema = this.faqs().length ? faqPageSchema(this.faqs()) : null;
    if (faqSchema) schema.push(faqSchema);
    this.seo.setJsonLd(schema);
  }

  /**
   * A store's products page: `/collection/<collection>/<storeKey>`.
   *
   * There is no fallback any more. `/deals/<storeKey>` was the same page at a second address and
   * has been retired to 410, so a store with no collection has nowhere to point and gets no link.
   */
  private storePath(storeKey: string, categorySlug?: string): string {
    const key = (storeKey ?? '').trim();
    if (!key) return '';
    const collection = (categorySlug ?? '').trim();
    return collection ? `/collection/${collection}/${encodeURIComponent(key)}` : '';
  }
}
